import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faFacebook, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import styles from "./LandingFooter.module.css";
import { useTheme } from "../../../../context/themeContext";
import {
  OPTIONAL_FOOTER_DOCUMENTS,
  resolveLegalDocumentUrl,
  shouldOpenInNewTab,
  toPublicAssetHref,
} from "../../../../config/legalDocuments";
import useOptionalDocuments from "../../../../hooks/useOptionalDocuments";
import { normalizeSiteFooter } from "../../../../config/siteFooter";
import {
  LEGAL_ENTITY,
  getLegalCopyrightLine,
  getLegalOperatorLine,
} from "../../../../config/legalEntity";
import logoLight from "../../../../assets/logos/Logo_Gold_black.png";
import logoDark from "../../../../assets/logos/Logo_Gold.png";

const SOCIAL_ICONS = {
  instagram: faInstagram,
  facebook: faFacebook,
  linkedin: faLinkedin,
};

/**
 * @param {object} props
 * @param {object} props.footer
 */
const LandingFooter = ({ footer: footerProp }) => {
  const { theme } = useTheme();
  const logo = theme === "dark" ? logoDark : logoLight;
  const year = new Date().getFullYear();
  const footer = normalizeSiteFooter(footerProp);
  const availableDocs = useOptionalDocuments();

  /** URL efectiva: la del link o, si es un documento opcional, su PDF cuando ya existe. */
  const linkUrl = (link) => {
    if (link.url || !link.optional_doc) return link.url;
    if (!availableDocs[link.optional_doc]) return "";
    const doc = OPTIONAL_FOOTER_DOCUMENTS.find((d) => d.key === link.optional_doc);
    return doc?.file || "";
  };

  const renderLink = (link) => {
    const url = linkUrl(link);
    if (!url) {
      return <span className={styles.footerLinkInactive}>{link.label}</span>;
    }

    const href = toPublicAssetHref(
      link.optional_doc ? url : resolveLegalDocumentUrl(url, link.label)
    );
    const openNewTab = shouldOpenInNewTab(href);
    const className = styles.footerLink;

    if (openNewTab) {
      return (
        <a
          href={href}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.label}
        </a>
      );
    }

    if (href.startsWith("http")) {
      return (
        <a href={href} className={className} rel="noreferrer">
          {link.label}
        </a>
      );
    }

    return (
      <Link to={href} className={className}>
        {link.label}
      </Link>
    );
  };

  const hasTrustLogos =
    footer.payment_logos?.length > 0 || footer.regulatory_logos?.length > 0;

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.brandCol}>
          <img src={logo} alt="Gold Loterías" className={styles.logo} />
          <p className={styles.brandDesc}>{footer.company_description}</p>
          <p className={styles.legalIdentity}>
            Operado por <strong>{LEGAL_ENTITY.legalName}</strong>
            <br />
            NIT {LEGAL_ENTITY.nit}
          </p>
        </div>
        {footer.columns?.map((col) => (
          <div key={col.title} className={styles.linksCol}>
            <h3 className={styles.colTitle}>{col.title}</h3>
            <ul className={styles.linkList}>
              {col.links?.map((link) => (
                <li key={link.url + link.label}>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.linkIcon}
                  />
                  {renderLink(link)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {hasTrustLogos && (
        <div className={styles.logosBar}>
          {footer.regulatory_logos?.map((logoItem) => (
            <img
              key={logoItem.image_url || logoItem.alt}
              src={logoItem.image_url}
              alt={logoItem.alt || "Entidad reguladora"}
              className={styles.trustLogo}
            />
          ))}
          {footer.payment_logos?.map((logoItem) => {
            const alt = String(logoItem.alt || "");
            const prominent = /pse|nequi/i.test(alt);
            return (
              <img
                key={logoItem.image_url || logoItem.alt}
                src={logoItem.image_url}
                alt={logoItem.alt || "Medio de pago"}
                className={`${styles.trustLogo} ${
                  prominent ? styles.trustLogoProminent : ""
                }`}
              />
            );
          })}
        </div>
      )}

      {footer.social_links?.length > 0 && (
        <div className={styles.socialBar}>
          <span className={styles.socialLabel}>Síguenos</span>
          <div className={styles.social}>
            {footer.social_links.map((s) => {
              const icon = SOCIAL_ICONS[s.icon_key];
              if (!icon) return null;
              return (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className={styles.socialLink}
                >
                  <FontAwesomeIcon icon={icon} />
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.bottomBar}>
        <p>{getLegalCopyrightLine(year)}</p>
        <p className={styles.legalOperator}>{getLegalOperatorLine()}</p>
        <p className={styles.legalAddress}>{LEGAL_ENTITY.address}</p>
      </div>
    </footer>
  );
};

export default LandingFooter;
